import React, {Fragment} from 'react';
import {Filter} from 'presentation/components/Filter';
import {translations} from 'presentation/translations-new';
import {Stack, Button} from '@mui/material';
import {SkeletonData} from 'presentation/components/ChangeData/SkeletonData';
import {
	EmptySearchState,
	StyledCard,
	MainFooter,
	SentinelLoading,
	AlertModal,
} from 'presentation/components';
import {SimpleConfirmationDrawer} from 'presentation/pages/private/booking/components/ConfirmationDrawer/SimpleConfirmationDrawer';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import {ErrorLoadingData} from 'presentation/components/ChangeData/ErrorLoadingData';
import {ModalError} from 'presentation/components/ModalError';
import {AlertInfo} from 'presentation/components/AlertInfo';
import {Container, FiltersContainer, GridContainer} from '../styles';
import {useReferralsFlow} from '../hooks/useReferralsFlow';
import {ReferralCard} from '../components/ReferralCard';
import {DateFilter} from '../components/DateFilter';
import {TypeFilter} from '../components/TypeFilter';
import {ReferralDetailsDrawer} from '../components/ReferralDetailsDrawer';
import {useReferralsList} from './Hooks/useReferralsList';
import {MyReferralsTaggingEnum} from 'main/analytics/gaTagsEnum';

const {
	searchReferrals,
	errorLoadingTitle,
	errorLoadingDescription,
	noReferralsFoundTitle,
	noReferralsFoundDescription,
	requestAuthorizationButton,
	clearSearchButton,
	clearFiltersButton,
	operatingHoursTitle,
	alert,
} = translations['pt-br'].privateFlow.myReferrals;
const {notice, tryAgain, agreementText, close} = translations['pt-br'].commons;
const {
	scheduleErrorModal: {scheduleErrorTitle, scheduleErrorDescription},
} = translations['pt-br'].privateFlow.requestAuthorizations;

export function ReferralsList() {
	const {DefaultParams, Filters} = useReferralsList();
	const {
		flowState,
		handleFilter,
		data,
		loadMore,
		openConfirmationDrawerState,
		setOpenConfirmationDrawerState,
		selectedReferralCdSenha,
		handleRetryAuthShedule,
		setSelectedReferralCdSenha,
		handleOpenDetails,
		abortDebounce,
		clearSearchParams,
		refresh,
		getReferralsParamsState,
		debouncedQueryFunc,
		searchValueState,
		handleConfirmSchedule,
		modalDescriptionState,
		IS_ERROR,
		IS_UNAVAILABLE,
		handleResetOperationState,
		hideFilter,
		clearFilters,
		isDateFilterActive,
		showFilterCount,
		isFetchingNextPage,
		showSearch,
		isMobile,
	} = useReferralsFlow({filters: DefaultParams, queryKey: QUERY_KEYS.GET_CURRENT_REFERRALS});

	return (
		<Fragment>
			{showSearch ? (
				<Filter.Root>
					<Filter.Search
						dataTagId={
							MyReferralsTaggingEnum.CLICK_BUSCAR_ENCAMINHAMENTO_EM_ANDAMENTO_MEUS_ENCAMINHAMENTOS
						}
						handleChange={() => {
							abortDebounce();
							debouncedQueryFunc();
						}}
						clearSearch={clearSearchParams}
						value={searchValueState}
						placeholder={searchReferrals}
					/>
				</Filter.Root>
			) : null}
			<Container>
				{flowState === 'error' ? (
					<Stack marginTop="24px">
						<ErrorLoadingData
							alternativeTitle={errorLoadingTitle}
							alternativeDescription={errorLoadingDescription}
							resetErrorBoundary={() => refresh()}
						/>
					</Stack>
				) : (
					<Stack gap="24px">
						<FiltersContainer>
							<DateFilter
								dataTagId={{
									openButton:
										MyReferralsTaggingEnum.CLICK_FILTRO_DATA_EM_ANDAMENTO_MEUS_ENCAMINHAMENTOS,
									closeButton:
										MyReferralsTaggingEnum.CLICK_FECHAR_FILTRO_DATA_EM_ANDAMENTO_MEUS_ENCAMINHAMENTOS,
									clearButton:
										MyReferralsTaggingEnum.CLICK_LIMPAR_FILTRO_DATA_EM_ANDAMENTO_MEUS_ENCAMINHAMENTOS,
									confirmButton:
										MyReferralsTaggingEnum.CLICK_APLICAR_FILTRO_DATA_EM_ANDAMENTO_MEUS_ENCAMINHAMENTOS,
								}}
								handleFilter={handleFilter}
								firstDate={getReferralsParamsState.dtInicio}
								secondDate={getReferralsParamsState.dtFim}
							/>
							{hideFilter ? null : (
								<TypeFilter
									dataTagId={{
										openBtn:
											MyReferralsTaggingEnum.CLICK_FILTRO_EM_ANDAMENTO_MEUS_ENCAMINHAMENTOS,
										closeBtn:
											MyReferralsTaggingEnum.CLICK_FECHAR_FILTRO_EM_ANDAMENTO_MEUS_ENCAMINHAMENTOS,
										clearBtn:
											MyReferralsTaggingEnum.CLICK_LIMPAR_FILTRO_EM_ANDAMENTO_MEUS_ENCAMINHAMENTOS,
										applyBtn:
											MyReferralsTaggingEnum.CLICK_APLICAR_FILTRO_EM_ANDAMENTO_MEUS_ENCAMINHAMENTOS,
									}}
									showCount={showFilterCount}
									filters={Filters}
									defaultFilters={DefaultParams}
									handleFilter={handleFilter}
									currentFilters={getReferralsParamsState}
								/>
							)}
						</FiltersContainer>

						{flowState !== 'empty' ? (
							<AlertInfo.Root>
								<AlertInfo.Description>{alert}</AlertInfo.Description>
							</AlertInfo.Root>
						) : null}

						{flowState === 'loading' ? (
							<GridContainer>
								<StyledCard.Container>
									<SkeletonData type="info" />
								</StyledCard.Container>
								<StyledCard.Container>
									<SkeletonData type="info" />
								</StyledCard.Container>
								<StyledCard.Container>
									<SkeletonData type="info" />
								</StyledCard.Container>
							</GridContainer>
						) : null}

						{flowState === 'empty' ? (
							<EmptySearchState
								dataTagId={
									MyReferralsTaggingEnum.VISUALIZOU_EMPTY_STATE_MEUS_ENCAMINHAMENTOS_EM_ANDAMENTO
								}
								customMargin="0"
								title={noReferralsFoundTitle}
								description={noReferralsFoundDescription}
								customJustifyContent="center"
								customMaxWidthCard="100%"
								noMarginImage>
								<Button
									data-tag-id={
										MyReferralsTaggingEnum.CLICK_SOLICITAR_AUTORIZACAO_EMPTY_STATE_MEUS_ENCAMINHAMENTOS_EM_ANDAMENTO
									}
									color="primary"
									onClick={() => setOpenConfirmationDrawerState('AUTHORIZATION')}>
									{requestAuthorizationButton}
								</Button>
								{searchValueState ? (
									<Button
										color="primary"
										onClick={() => clearSearchParams()}
										variant="outlined"
										data-tag-id={
											MyReferralsTaggingEnum.CLICK_LIMPAR_BUSCA_EMPTY_STATE_MEUS_ENCAMINHAMENTOS_EM_ANDAMENTO
										}>
										{clearSearchButton}
									</Button>
								) : null}
								{(!searchValueState && !hideFilter) || isDateFilterActive ? (
									<Button
										color="primary"
										onClick={() => clearFilters()}
										variant="outlined"
										data-tag-id={
											MyReferralsTaggingEnum.CLICK_LIMPAR_FILTROS_EMPTY_STATE_MEUS_ENCAMINHAMENTOS_EM_ANDAMENTO
										}>
										{clearFiltersButton}
									</Button>
								) : null}
							</EmptySearchState>
						) : null}
						<Fragment>
							<SimpleConfirmationDrawer
								handleConfirmAndContinue={() => handleConfirmSchedule()}
								isOpened={openConfirmationDrawerState !== undefined}
								handleOpen={() => null}
								handleClose={() => setOpenConfirmationDrawerState(undefined)}
							/>
							<ReferralDetailsDrawer
								onOpen={() => null}
								onClose={() => setSelectedReferralCdSenha(undefined)}
								open={!!selectedReferralCdSenha}
								cdSenha={selectedReferralCdSenha}
							/>
							<GridContainer>
								{data?.map((item, index) => (
									<ReferralCard
										key={`${item.cdSenha}-${index}`}
										data={item}
										handleOpenSchedule={() =>
											setOpenConfirmationDrawerState(item.tipoProcedimento)
										}
										handleOpenDetails={referral => handleOpenDetails(referral)}
									/>
								))}
							</GridContainer>
							<SentinelLoading
								minHeight="200px"
								showLoadingText={false}
								onVisible={() => {
									if (data?.length > 0) {
										loadMore();
									}
								}}
								isLoading={isFetchingNextPage}
							/>
						</Fragment>
					</Stack>
				)}
				<AlertModal
					onClose={handleResetOperationState}
					open={IS_UNAVAILABLE}
					headerTitle={notice}
					title={operatingHoursTitle}
					description={modalDescriptionState}
					variant="info"
					buttonLabel={agreementText}
				/>
				<ModalError
					onClose={handleResetOperationState}
					headerTitle={notice}
					description={scheduleErrorDescription}
					open={IS_ERROR}
					title={scheduleErrorTitle}
					firstButton={{
						title: tryAgain,
						handleClick: () => {
							handleRetryAuthShedule();
						},
					}}
					secondButton={{
						title: close,
						handleClick: () => handleResetOperationState(),
					}}
				/>
				<MainFooter noMargin={!isMobile} />
			</Container>
		</Fragment>
	);
}
